/**
 * AuthContext — Steward Admin
 * Firebase Auth with steward role verification.
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth } from '../services/firebase';
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from 'firebase/auth';
import { authenticateSteward } from '../services/tripleCrown';

const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [stewardInfo, setStewardInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const result = await authenticateSteward();
          setUser(firebaseUser);
          setStewardInfo(result.data);
          setError('');
        } catch (err) {
          // Not a steward — sign them out
          setUser(null);
          setStewardInfo(null);
          setError('Access denied — steward role required');
          await signOut(auth);
        }
      } else {
        setUser(null);
        setStewardInfo(null);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  const login = async (email, password) => {
    setError('');
    try {
      await signInWithEmailAndPassword(auth, email, password);
      // onAuthStateChanged will handle steward verification
    } catch (err) {
      setError(err.code === 'auth/invalid-credential'
        ? 'Invalid email or password'
        : err.message);
      throw err;
    }
  };

  const logout = async () => {
    await signOut(auth);
    setUser(null);
    setStewardInfo(null);
  };

  return (
    <AuthContext.Provider value={{ user, stewardInfo, loading, error, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
